import React from 'react';

import Container from '@amzn/awsui-components-react/polaris/container';
import Header from '@amzn/awsui-components-react/polaris/header';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Input from '@amzn/awsui-components-react/polaris/input';
import Textarea from '@amzn/awsui-components-react/polaris/textarea';
import Multiselect, { MultiselectProps } from '@amzn/awsui-components-react/polaris/multiselect';
import Button from '@amzn/awsui-components-react/polaris/button';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import Grid from '@amzn/awsui-components-react/polaris/grid';
import Popover from '@amzn/awsui-components-react/polaris/popover';
import Link from '@amzn/awsui-components-react/polaris/link';

import { AmbassadorSelect } from './ambassadors';
import { CauseAutosuggest } from './causeAutosuggest';

import { getOptionFromMultiValues } from '../../../utils/url';
import {
  EditableInfoField,
  optionsToValueArrayString,
} from '../../../hooks/events/editableEventInfo';

import { externalLinks } from '../../../data/externalLinks';
import { CommunityOrganizationHelpPopover } from '../../events/createEvent/components/CommunityOrganizationHelpInfo';
import { useLocalizedText } from '../../../hooks/localizedText';
import { LocalizedTrans } from '../../../hooks/localization/localizedTrans';
import { useTOptions } from '../../../hooks/localization/tOptions';
import { ELG_SPONSORS } from '../../../data/eventOptionLists';

type AdditionalInfoProps = {
  editableInfo: { [s in EditableInfoField]: string };
  updateEditableInfoFields: (fields: { [s in EditableInfoField]?: string }) => void;
  eventErrors?: { [s in EditableInfoField]?: string };
  eventWarnings?: { [s in EditableInfoField]?: string };
};

function OptionalLabel({ children }: { children: string }) {
  const namespaces = ['attendance', 'events', 'translation'];
  const { t } = useLocalizedText(namespaces);

  return (
    <span>
      {children} - <i>{t('additionalInfo.optional')}</i>
    </span>
  );
}

export function AdditionalInfo({
  editableInfo,
  updateEditableInfoFields,
  eventErrors,
}: AdditionalInfoProps) {
  const namespaces = ['attendance', 'translation'];
  const { t } = useLocalizedText(namespaces);

  const sponsors: MultiselectProps.Options = useTOptions(namespaces, ELG_SPONSORS);

  return (
    <Container header={<Header variant="h2">{t('additionalInfo.header')}</Header>}>
      <SpaceBetween direction="vertical" size="l">
        <FormField
          stretch
          label={
            <SpaceBetween direction="horizontal" size="l">
              <OptionalLabel>{t('additionalInfo.additionalCoordinators')}</OptionalLabel>
              <Popover
                dismissButton={false}
                position="top"
                size="large"
                triggerType="custom"
                header={t('additionalInfo.communityAmbassadors.header')}
                content={
                  <SpaceBetween direction="horizontal" size="s">
                    <span style={{ whiteSpace: 'pre-line' }}>
                      {t('additionalInfo.communityAmbassadors.content')}
                    </span>
                    <Link href={externalLinks.attendance.becomeAmbassador} external>
                      {t('additionalInfo.communityAmbassadors.learnMore')}
                    </Link>
                  </SpaceBetween>
                }
              >
                <Button variant="inline-link">
                  {t('grant.infoButton', { ns: 'translation' })}
                </Button>
              </Popover>
            </SpaceBetween>
          }
          description={t('additionalInfo.ambassadorsDescription')}
          errorText={eventErrors?.registered_community_ambassadors}
        >
          <AmbassadorSelect
            currentAmbassadors={editableInfo.registered_community_ambassadors}
            onChange={({ detail }) => {
              updateEditableInfoFields({
                registered_community_ambassadors: optionsToValueArrayString(detail.selectedOptions),
              });
            }}
          />
        </FormField>

        <FormField
          info={<CommunityOrganizationHelpPopover />}
          stretch
          label={<OptionalLabel>{t('additionalInfo.communityOrganization')}</OptionalLabel>}
          errorText={eventErrors?.community_organization}
        >
          <CauseAutosuggest
            currentCause={editableInfo.community_organization}
            onChange={({ detail }) =>
              updateEditableInfoFields({ community_organization: detail.value })
            }
          />
        </FormField>

        <FormField
          stretch
          label={<OptionalLabel>{t('additionalInfo.organizationPointOfContact')}</OptionalLabel>}
        >
          <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
            <FormField
              stretch
              label={t('additionalInfo.organizationContactName')}
              errorText={eventErrors?.organization_point_of_contact_name}
            >
              <Input
                value={editableInfo.organization_point_of_contact_name}
                onChange={({ detail }) =>
                  updateEditableInfoFields({ organization_point_of_contact_name: detail.value })
                }
              />
            </FormField>
            <FormField
              stretch
              label={t('additionalInfo.organizationContactEmail')}
              errorText={eventErrors?.organization_point_of_contact_email}
            >
              <Input
                value={editableInfo.organization_point_of_contact_email}
                onChange={({ detail }) =>
                  updateEditableInfoFields({ organization_point_of_contact_email: detail.value })
                }
                type="email"
              />
            </FormField>
          </Grid>
        </FormField>

        <FormField
          stretch
          label={<OptionalLabel>{t('additionalInfo.engagementRecap')}</OptionalLabel>}
          description={t('additionalInfo.engagementRecapDescription')}
          errorText={eventErrors?.engagement_recap}
        >
          <Textarea
            value={editableInfo.engagement_recap}
            onChange={({ detail }) => updateEditableInfoFields({ engagement_recap: detail.value })}
            rows={4}
          />
        </FormField>

        <FormField
          stretch
          label={
            <SpaceBetween direction="horizontal" size="s">
              <OptionalLabel>{t('additionalInfo.elgSponsors')}</OptionalLabel>
              <Popover
                dismissButton={false}
                position="top"
                size="large"
                triggerType="custom"
                header={t('additionalInfo.elgSponsorsInfo.header')}
                content={
                  <SpaceBetween direction="horizontal" size="s">
                    <span style={{ whiteSpace: 'pre-line' }}>
                      <LocalizedTrans
                        i18nKey="attendance:additionalInfo.elgSponsorsInfo.content"
                        components={{
                          link: <Link external href={externalLinks.attendance.becomeAmbassador} />,
                        }}
                      />
                    </span>
                  </SpaceBetween>
                }
              >
                <Button variant="inline-link">
                  {t('grant.infoButton', { ns: 'translation' })}
                </Button>
              </Popover>
            </SpaceBetween>
          }
          errorText={eventErrors?.elg_sponsors}
        >
          <Multiselect
            onChange={({ detail }) => {
              updateEditableInfoFields({
                elg_sponsors: optionsToValueArrayString(detail.selectedOptions),
              });
            }}
            selectedOptions={getOptionFromMultiValues(editableInfo.elg_sponsors, sponsors)}
            options={sponsors}
          />
        </FormField>
        <FormField
          stretch
          label={
            <SpaceBetween direction="horizontal" size="l">
              <OptionalLabel>{t('additionalInfo.photoUpload')}</OptionalLabel>
              <Popover
                dismissButton={false}
                position="top"
                size="small"
                triggerType="custom"
                header={t('additionalInfo.photoInfo.header')}
                content={t('additionalInfo.photoInfo.content')}
              >
                <Button variant="inline-link">
                  {t('grant.infoButton', { ns: 'translation' })}
                </Button>
              </Popover>
            </SpaceBetween>
          }
          description={t('additionalInfo.photoUploadDescription')}
          errorText={eventErrors?.photo_upload}
        >
          <SpaceBetween direction="horizontal" size="l">
            <Input
              value={editableInfo.photo_upload}
              onChange={({ detail }) => updateEditableInfoFields({ photo_upload: detail.value })}
            />

            <Button
              href={externalLinks.attendance.workdocs}
              data-aci-analytics-name="trackAttendance_workDocsLink"
              iconAlign="right"
              iconName="external"
              target="_blank"
            >
              {t('additionalInfo.chooseFiles')}
            </Button>
          </SpaceBetween>
        </FormField>
      </SpaceBetween>
    </Container>
  );
}
